import React, { useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FiX } from 'react-icons/fi';

// Type-safe icon wrapper
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;

interface DeleteConfirmModalProps {
  title: string;
  message: string;
  confirmLabel?: string;
  isDeleting?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  title,
  message,
  confirmLabel = 'Delete',
  isDeleting = false,
  onCancel,
  onConfirm,
}) => {
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isDeleting) {
      onCancel();
    }
  };

  // Handle Escape key to close modal
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isDeleting) {
        onCancel();
      }
    },
    [isDeleting, onCancel]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const modal = (
    <div className="su-fundraise-modal-overlay" onClick={handleOverlayClick}>
      <div className="su-fundraise-modal" style={{ maxWidth: '400px' }} role="alertdialog" aria-modal="true" aria-labelledby="fundraise-delete-title" aria-describedby="fundraise-delete-message">
        <div className="su-fundraise-modal-header">
          <div>
            <h2 id="fundraise-delete-title" className="su-fundraise-modal-title">{title}</h2>
          </div>
          <button
            className="su-fundraise-modal-close"
            onClick={onCancel}
            disabled={isDeleting}
            aria-label="Close modal"
          >
            <CloseIcon width={20} height={20} />
          </button>
        </div>
        <div className="su-fundraise-modal-body">
          <p id="fundraise-delete-message" style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
            {message}
          </p>
        </div>
        <div className="su-fundraise-modal-footer">
          <button
            className="su-btn-secondary"
            onClick={onCancel}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            className="su-btn-danger"
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default DeleteConfirmModal;
